import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getGuest, signOutGuest, isIpadMode } from "@/lib/guest";
import { publish as publishLive } from "@/lib/ipadLiveBus";

/**
 * Global kick-watcher for the shared cabin iPad.
 *
 * Mounted once at the App root so every guest route (menu / reservations
 * / journey / cabin / booking / confirmation) shares one control
 * channel. The previous per-CabinView listener only fired when the
 * guest happened to be on that page, which is why "kick from admin"
 * appeared to fail: the iPad was on `/menu`, not listening.
 *
 * A subtlety worth calling out: `<IpadKillMount />` mounts BEFORE the
 * guest logs into iPad mode, so `isIpadMode()` returns false at that
 * moment. If we used `useEffect(..., [])` the hook would set nothing
 * up and never revisit the decision. Instead we treat `isIpadMode()`
 * as external state, re-read it on every route change, and only spin
 * up the WS + poll once it flips true. Cleanup runs when it flips
 * back to false (e.g., after a successful eviction).
 *
 * Two independent eviction paths so ONE always wins:
 *   1. WebSocket `/api/ws/ipad` — delivers `ipad.session_killed`
 *      instantly, before the backend nulls the DB token.
 *   2. 8-second REST poll of `/public/ipad/session` — if the socket
 *      never opened (proxy blocked it) or dropped silently, the poll
 *      picks up the 401 the backend serves once the token is cleared.
 */
export function useIpadKillWatcher() {
  const navigate = useNavigate();
  const location = useLocation();
  // Cache the "am I iPad?" state. Flip on route changes; the main
  // effect depends only on this flag, so it doesn't churn WS on every
  // navigation once it's already up.
  const [active, setActive] = useState(() => isIpadMode());
  useEffect(() => {
    const cur = isIpadMode();
    if (cur !== active) setActive(cur);
  }, [location.pathname, active]);

  const evictedRef = useRef(false);

  useEffect(() => {
    if (!active) return;
    const g = getGuest();
    if (!g.cabin_id || !g.ipad_token) return;
    evictedRef.current = false;

    const evict = (why) => {
      if (evictedRef.current) return;
      evictedRef.current = true;
      // eslint-disable-next-line no-console
      console.info("[ipad-kick]", why);
      toast.info("This iPad was signed out by the crew.");
      signOutGuest();
      // Short delay so the toast is visible; navigate replaces so the
      // guest can't back-button their way into the just-killed session.
      setTimeout(() => navigate("/", { replace: true }), 700);
    };

    // ------- (1) WebSocket path -------
    let closed = false;
    let ws = null;
    let retryTimer = null;
    const openSocket = () => {
      if (closed || evictedRef.current) return;
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      if (!backend) return;
      const wsUrl =
        backend.replace(/^http/, "ws") +
        `/api/ws/ipad?cabin_id=${encodeURIComponent(g.cabin_id)}` +
        `&session_token=${encodeURIComponent(g.ipad_token)}`;
      try {
        ws = new WebSocket(wsUrl);
      } catch {
        retryTimer = setTimeout(openSocket, 5000);
        return;
      }
      ws.onopen = () => {
        // eslint-disable-next-line no-console
        console.info("[ipad-kick] WS connected", wsUrl);
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (!msg) return;
          if (msg.type === "ipad.session_killed") { evict("WS ipad.session_killed"); return; }
          // Every other server-pushed frame — charter.updated,
          // booking.<kind>, cabin.updated, etc — is republished on
          // the in-process bus so subscriber pages silent-refetch.
          if (msg.type && msg.type !== "hello") publishLive(msg);
        } catch { /* ignore malformed */ }
      };
      ws.onerror = () => { /* onclose will follow */ };
      ws.onclose = () => {
        if (closed || evictedRef.current) return;
        // Reconnect on transient closes; the REST poll will still
        // catch a real eviction while we're between sockets.
        retryTimer = setTimeout(openSocket, 5000);
      };
    };
    openSocket();

    // ------- (2) REST poll fallback -------
    const poll = async () => {
      if (closed || evictedRef.current) return;
      try {
        await api.post("/public/ipad/session", {
          cabin_id: g.cabin_id,
          session_token: g.ipad_token,
        });
      } catch (e) {
        if (e?.response?.status === 401) evict("REST 401");
      }
    };
    // 8s cadence — snappy enough for a "kick" to feel immediate even
    // when the WS never opens (some corporate proxies strip WS
    // upgrades), still light on the backend.
    const pollTimer = setInterval(poll, 8000);
    const onVis = () => { if (document.visibilityState === "visible") poll(); };
    document.addEventListener("visibilitychange", onVis);
    // Fire once right away so if the token was already killed before
    // this hook activated (rare), we evict on the next micro-task.
    poll();

    return () => {
      closed = true;
      if (retryTimer) clearTimeout(retryTimer);
      clearInterval(pollTimer);
      document.removeEventListener("visibilitychange", onVis);
      if (ws) { try { ws.close(); } catch { /* noop */ } }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}
